import { filter, uniq } from 'lodash';

import urls from '../constants/urls';

import { gatherErrors } from '../utils/errorHandling';

import { paginationParams } from '../utils/pagination';

import { removeFlash, addFlash } from './flashes';

export const LOADING_NUMBERS = 'numbers/LOADING_NUMBERS';
export const NUMBERS_LOAD_FAILED = 'numbers/NUMBERS_LOAD_FAILED';
export const NUMBERS_LOAD_SUCCESS = 'numbers/NUMBERS_LOAD_SUCCESS';

export const CREATE_NUMBER_FAILED = 'numbers/CREATE_NUMBER_FAILED';
export const CREATE_NUMBER_SUCCESS = 'numbers/CREATE_NUMBER_SUCCESS';
export const CREATING_NUMBER = 'numbers/CREATING_NUMBER';
export const CREATE_NUMBER_FAILED_DELAYED =
  'numbers/CREATE_NUMBER_FAILED_DELAYED';

export const CHECKING_UNIQUENESS = 'numbers/CHECKING_UNIQUENESS';
export const UNIQUENESS = 'numbers/UNIQUENESS';
export const CHECKING_UNIQUENESS_FAILED = 'numbers/CHECKING_UNIQUENESS_FAILED';

export const DELETE_NUMBER_FAILED = 'number/DELETE_NUMBER_FAILED';
export const DELETE_NUMBER_SUCCESS = 'number/DELETE_NUMBER_SUCCESS';
export const DELETING_NUMBER = 'number/DELETING_NUMBER';

export const EXPORTING_NUMBERS = 'numbers/EXPORTING_NUMBERS';
export const NUMBERS_EXPORT_FAILED = 'numbers/NUMBERS_EXPORT_FAILED';
export const NUMBERS_EXPORT_SUCCESS = 'numbers/NUMBERS_EXPORT_SUCCESS';

export const LOCKING_NUMBER = 'number/LOCKING_NUMBER';
export const LOCKING_NUMBER_SUCCESS = 'number/LOCKING_NUMBER_SUCCESS';
export const LOCKING_NUMBER_FAILED = 'number/LOCKING_NUMBER_FAILED';

export const UNLOCKING_NUMBER = 'number/UNLOCKING_NUMBER';
export const UNLOCKING_NUMBER_SUCCESS = 'number/UNLOCKING_NUMBER_SUCCESS';
export const UNLOCKING_NUMBER_FAILED = 'number/UNLOCKING_NUMBER_FAILED';

export const NUMBER_IS_LOCKED = 'number_NUMBER_IS_LOCKED';

const initialState = {
  initialLoading: true,

  numbers: [],

  failedCreating: false,
  failedLoading: false,
  failedExporting: false,

  hasLoaded: false,

  isCreating: false,
  isLoading: false,
  isExporting: false,

  page: 1,
  pages: 1,
  total: 0,

  exportedFile: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case LOADING_NUMBERS:
      return {
        ...state,
        failedLoading: false,
        isLoading: true,
      };

    case NUMBERS_LOAD_SUCCESS:
      return {
        ...state,
        failedLoading: false,
        hasLoaded: true,
        initialLoading: false,
        isLoading: false,
        numbers: action.payload.data.data,
        ...paginationParams(
          action.payload.headers['link'],
          action.payload.headers['x-total']
        ),
      };

    case NUMBERS_LOAD_FAILED:
      return {
        ...state,
        failedLoading: true,
        isLoading: false,
      };

    case CREATING_NUMBER:
      return {
        ...state,
        failedCreating: false,
        isCreating: true,
      };

    case CREATE_NUMBER_SUCCESS:
      return {
        ...state,
        isCreating: false,
      };

    case CREATE_NUMBER_FAILED:
      return {
        ...state,
        createErrors: gatherErrors(action.error, 'create_number_failed'),
        failedCreating: true,
        isCreating: false,
      };

    case DELETING_NUMBER:
      return {
        ...state,
        failedDelete: false,
        isDeleting: true,
      };

    case DELETE_NUMBER_SUCCESS:
      const newNumbers = uniq(
        filter(
          state.numbers,
          number => number.id !== action.meta.previousAction.number.id
        )
      );

      return {
        ...state,
        numbers: newNumbers,
        isDeleting: false,
      };

    case DELETE_NUMBER_FAILED:
      return {
        ...state,
        failedDelete: true,
        isDeleting: false,
      };

    case EXPORTING_NUMBERS:
      return {
        ...state,
        isExporting: true,
      };

    case NUMBERS_EXPORT_SUCCESS:
      return {
        ...state,
        isExporting: false,
        failedExporting: false,
        exportedFile: action.payload.data.data.file,
      };

    case NUMBERS_EXPORT_FAILED:
      return {
        ...state,
        isExporting: false,
        failedExporting: true,
        exportedFile: null,
      };

    default:
      return state;
  }
};

export const loadNumbers = (hospital, page, searchTerm) => dispatch => {
  return dispatch({
    types: [LOADING_NUMBERS, NUMBERS_LOAD_SUCCESS, NUMBERS_LOAD_FAILED],
    payload: {
      request: {
        url: urls.hospitals.numbers.index(hospital.id),
        params: { page, search_string: searchTerm },
      },
    },
  });
};

export const createNumber = (hospitalId, params) => dispatch => {
  const numberParams = {
    extension: params.extension,
    name: params.name,
    number_type: params.number_type,
    marked_incorrect_counter: params.marked_incorrect_counter,
  };

  return dispatch({
    types: [CREATING_NUMBER, CREATE_NUMBER_SUCCESS, CREATE_NUMBER_FAILED],
    payload: {
      request: {
        method: 'post',
        url: urls.hospitals.numbers.create(hospitalId),
        data: { number: numberParams },
      },
    },
  });
};

export const addCreateNumberFailedFlash = () => dispatch => {
  addFlash(CREATE_NUMBER_FAILED_DELAYED)(dispatch);
  removeFlash('success', 'Could not create number', dispatch);
};

export const checkUniqueness = (hospitalId, params) => dispatch => {
  const numberParams = {
    extension: params.extension,
    name: params.name,
    number_type: params.number_type,
  };

  return dispatch({
    types: [CHECKING_UNIQUENESS, UNIQUENESS, CHECKING_UNIQUENESS_FAILED],
    payload: {
      request: {
        method: 'get',
        url: urls.hospitals.numbers.new(hospitalId),
        params: numberParams,
      },
    },
  });
};

export const lockNumber = (hospital, number) => dispatch => {
  return dispatch({
    types: [LOCKING_NUMBER, LOCKING_NUMBER_SUCCESS, LOCKING_NUMBER_FAILED],
    payload: {
      request: {
        method: 'patch',
        url: urls.hospitals.numbers.lock(hospital.id, number.id),
        data: {
          number: {
            locked: true,
          },
        },
      },
    },
  });
};

export const unlockNumber = (hospital, number) => dispatch => {
  return dispatch({
    types: [
      UNLOCKING_NUMBER,
      UNLOCKING_NUMBER_SUCCESS,
      UNLOCKING_NUMBER_FAILED,
    ],
    payload: {
      request: {
        method: 'patch',
        url: urls.hospitals.numbers.lock(hospital.id, number.id),
        data: {
          number: {
            locked: false,
          },
        },
      },
    },
  });
};

export const deleteNumber = (hospital, number) => dispatch => {
  return dispatch({
    types: [DELETING_NUMBER, DELETE_NUMBER_SUCCESS, DELETE_NUMBER_FAILED],
    payload: {
      request: {
        method: 'delete',
        url: urls.hospitals.numbers.destroy(hospital.id, number.id),
      },
    },
    number: number,
  });
};

export const exportNumbers = hospital => dispatch => {
  return dispatch({
    types: [EXPORTING_NUMBERS, NUMBERS_EXPORT_SUCCESS, NUMBERS_EXPORT_FAILED],
    payload: {
      request: {
        url: urls.hospitals.numbersExport.index(hospital.id),
      },
    },
  });
};
